import type { IntlDateTimeFormats } from 'vue-i18n';
import messages from '@intlify/unplugin-vue-i18n/messages';
import moment from 'moment';
import { createI18n } from 'vue-i18n';
import * as vuetifyLocales from 'vuetify/locale';
import { useAuth } from '@/composables/useAuth';

const { authUser } = useAuth();
const permittedLocales: string[] = ['en', 'fr', 'es', 'it', 'pt_BR', 'el', 'de'] as const;
export type PermittedLocale = typeof permittedLocales[number];
const defaultLanguage: PermittedLocale = 'en';

export const availableLanguages: { locale: PermittedLocale; name: string }[] = [
  { locale: 'en', name: 'English' },
  { locale: 'fr', name: 'Français' },
  { locale: 'es', name: 'Español' },
  { locale: 'it', name: 'Italiano' },
  { locale: 'pt_BR', name: 'Português (Brasil)' },
  { locale: 'el', name: 'Ελληνικά' },
  { locale: 'de', name: 'Deutsch' },
];

const datetimeFormats: IntlDateTimeFormats = {
  en: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  fr: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  es: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  it: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  pt_BR: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    short_day: {
      weekday: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  el: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  de: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
};

const messagesWithVuetify: Record<string, Record<string, any>> = {};

if (messages) {
  Object.keys(messages).forEach((key) => {
    let k = key;
    if (k === 'pt_BR') {
      k = 'pt';
    }
    const vuetifyLocale = (vuetifyLocales as Record<string, unknown>)[k];

    const localeMessages = messages?.[key] as Record<string, unknown> | undefined;
    if (localeMessages != null && vuetifyLocale != null) {
      messagesWithVuetify[k] = {
        ...localeMessages,
        $vuetify: {
          ...vuetifyLocale as Record<string, unknown>,
          ...localeMessages.$vuetify as Record<string, unknown>,
        },
      };
    }
  });
}

function isLocalePermitted(locale: string): boolean {
  return permittedLocales.includes(locale);
}

function getPreferredLocaleOrUseBrowserSetting(): PermittedLocale {
  if (authUser?.preferred_language && isLocalePermitted(authUser?.preferred_language)) {
    return authUser.preferred_language;
  }
  if (navigator?.languages.length && isLocalePermitted(navigator.languages[0])) {
    const [locale] = navigator.languages;
    return locale;
  }

  if (Object.keys(navigator).includes('userLanguage')) {
    const { userLanguage } = navigator as unknown as { userLanguage: string };
    if (isLocalePermitted(userLanguage)) {
      return userLanguage;
    }
  }

  if (navigator.language && isLocalePermitted(navigator.language)) {
    return navigator.language;
  }

  if (Object.keys(navigator).includes('browserLanguage')) {
    const { browserLanguage } = navigator as unknown as { browserLanguage: string };
    if (isLocalePermitted(browserLanguage)) {
      return browserLanguage;
    }
  }

  return defaultLanguage;
}

export async function setMomentLocale(locale: string): Promise<void> {
  try {
    const momentLocale = locale.toLowerCase().replace('_', '-');
    if (momentLocale !== 'en') {
      await import(`moment/dist/locale/${momentLocale}.js`);
    }
    moment.locale(momentLocale);
  }
  catch (e) {
    moment.locale('en');
    console.warn(`Cannot load moment locale ${locale}`, e);
  }
}

export function initI18n() {
  const locale = getPreferredLocaleOrUseBrowserSetting();
  let realLocale = locale;
  if (locale.includes('_')) {
    realLocale = locale.replace('_', '-');
  }
  setMomentLocale(realLocale);
  return createI18n({
    legacy: false,
    locale: realLocale,
    fallbackLocale: defaultLanguage,
    messages: messagesWithVuetify,
    datetimeFormats,
    missingWarn: false,
    fallbackWarn: false,
  });
}
